"use client";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import Navbar from "../../../../../components/navbar";
import Footer from "../../../../../components/footer";
import styles from "../css/deliverydetails.module.css";
import FilterButtons from "../../../../../components/universal-filter-tabs";
import Pagination from "../../../../../components/Pagination";
import FormInput from "../../../../../components/FormInput";

import DeliveryModal from "../components/DeliveryModal";
import ConfirmationModal from "../components/ConfirmationModal";
import PriorityPill from "../components/PriorityPill";
import DeliveryStatsCardGrid from "../components/DeliveryStatsCardGrid";
import DeliveryTable from "../components/DeliveryTable";
import { fetchPendingDeliveries, fetchCompletedDeliveries, fetchInTransitDeliveries, fetchAssignedDeliveries, assignDeliveryDriver, assignDeliveryDriverWithAddress } from "../../../../../api/dispensary/Delivery/deliveryApi";

export function DeliveryDetails() {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState("Pending");
  const [currentPage, setCurrentPage] = useState(1);

  // Helper: format yyyy-mm-dd to dd/mm/yyyy for UI display only
  const formatDateDMY = (yyyyMmDd) => {
    const s = String(yyyyMmDd || '').trim();
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return s;
    return `${m[3]}/${m[2]}/${m[1]}`;
  };

  const [isDeliveryModalOpen, setIsDeliveryModalOpen] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [pendingDate, setPendingDate] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [assignedCount, setAssignedCount] = useState(0);

  // Reset pagination when date filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [pendingDate, fromDate, toDate]);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        setLoading(true);
        setError("");

        const [pendingRows, inTransitRows, assignedRows, completedRows] = await Promise.all([
          fetchPendingDeliveries(),
          fetchInTransitDeliveries(),
          fetchAssignedDeliveries(),
          fetchCompletedDeliveries(),
        ]);

        if (!isMounted) return;

        const safePending = Array.isArray(pendingRows) ? pendingRows : [];
        const safeInTransit = Array.isArray(inTransitRows) ? inTransitRows : [];
        const safeAssigned = Array.isArray(assignedRows) ? assignedRows : [];
        const safeCompleted = Array.isArray(completedRows) ? completedRows : [];

        setDeliveries([...safePending, ...safeInTransit, ...safeCompleted]);
        setAssignedCount(safeAssigned.length);

      } catch (e) {
        if (!isMounted) return;
        setError("Failed to load deliveries");
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, []);

  const filterButtons = ["Pending", "In Transit", "Completed"];
  const itemsPerPage = 5;

  // Selection helpers
  const isSelected = (id) => selectedIds.has(id);
  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const areAllCurrentSelected = (rows) => rows.every((r) => selectedIds.has(r.orderId));
  const toggleAllCurrent = (rows) => {
    const allSelected = areAllCurrentSelected(rows);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      rows.forEach((r) => {
        if (allSelected) next.delete(r.orderId); else next.add(r.orderId);
      });
      return next;
    });
  };

  // Columns per filter (Pending will be defined after paginatedData)

  const columnsInTransit = [
    { header: "Patient", accessor: "patient", width: "1.2fr" },
    { header: "Address", accessor: "address", width: "2fr" },
    { header: "Items", accessor: "items", width: "1.2fr" },
    { header: "Start Date", accessor: "startDate", width: "1fr", render: (value) => formatDateDMY(value) },
    { header: "Driver", accessor: "driver", width: "1fr" },
    {
      header: "Action",
      accessor: "action",
      width: "200px",
      render: (value, row) => (
        <div className={`${styles.actionCell} ${styles.center}`} onClick={(e) => e.stopPropagation()}>
          <button
            className={styles.viewButton}
            onClick={() =>
              navigate(`/dispensary/collection-and-delivery/delivery/${row.orderId}`, {
                state: { delivery: row },
              })
            }
          >
            View Delivery
          </button>
        </div>
      ),
    },
  ];

  const columnsCompleted = [
    { header: "Patient", accessor: "patient", width: "1.2fr" },
    { header: "Address", accessor: "address", width: "2fr" },
    { header: "Items", accessor: "items", width: "1.2fr" },
    { header: "Start Date", accessor: "startDate", width: "1fr", render: (value) => formatDateDMY(value) },
    { header: "End Date", accessor: "endDate", width: "1fr", render: (value) => formatDateDMY(value) },
    { header: "Driver", accessor: "driver", width: "1fr" },
    {
      header: "Action",
      accessor: "action",
      width: "260px", // fits View Delivery + View Invoice buttons
      render: (value, row) => (
        <div className={styles.actionCell} onClick={(e) => e.stopPropagation()}>
          <button
            className={styles.viewButton}
            onClick={() =>
              navigate(`/dispensary/collection-and-delivery/delivery/${row.orderId}`, {
                state: { delivery: row },
              })
            }
          >
            View Delivery
          </button>
          <button
            className={styles.viewButton}
            title="Coming soon"
            onClick={(e) => { e.stopPropagation(); /* no-op for now */ }}
          >
            View Invoice
          </button>
        </div>
      ),
    },
  ];

  const handleAssignDriver = (deliveryData) => {
    setSelectedDelivery(deliveryData);
    setIsDeliveryModalOpen(true);
  };

  const handleBatchAssignOpen = () => {
    setSelectedDelivery(null);
    setIsDeliveryModalOpen(true);
  };

  const handleSelectAddress = (orderId, nextIndex) => {
    setDeliveries((prev) =>
      prev.map((d) => {
        if (String(d.orderId) !== String(orderId)) return d;
        const addrs = Array.isArray(d?.deliveryAddresses) ? d.deliveryAddresses : [];
        const idx = Number.isFinite(nextIndex) ? nextIndex : 0;
        const nextAddress = (addrs[idx] || d.address || "").trim?.() || (addrs[idx] || d.address || "");
        return {
          ...d,
          address: nextAddress,
          selectedDeliveryAddressIndex: idx,
        };
      })
    );

    setSelectedDelivery((prev) => {
      if (!prev) return prev;
      if (String(prev.orderId) !== String(orderId)) return prev;
      const addrs = Array.isArray(prev?.deliveryAddresses) ? prev.deliveryAddresses : [];
      const idx = Number.isFinite(nextIndex) ? nextIndex : 0;
      const nextAddress = (addrs[idx] || prev.address || "").trim?.() || (addrs[idx] || prev.address || "");
      return {
        ...prev,
        address: nextAddress,
        selectedDeliveryAddressIndex: idx,
      };
    });
  };

  const handleDriverAssign = async (driver, deliveriesArrayOrSingle) => {
    try {
      const list = Array.isArray(deliveriesArrayOrSingle)
        ? deliveriesArrayOrSingle
        : deliveriesArrayOrSingle
        ? [deliveriesArrayOrSingle]
        : [];

      const orderIds = list.map((d) => d.orderId);
      const assignments = list.map((d) => ({
        orderId: d.orderId,
        chosenDeliveryAddress: d.address || "",
      }));

      if (driver?.id && orderIds.length) {
        console.log('🚚 [FRONTEND] Assigning driver:', {
          driverId: driver.id,
          driverName: driver.name,
          orderIds,
          orderCount: orderIds.length,
          orderIdsType: typeof orderIds,
          isArray: Array.isArray(orderIds)
        });

        const response = await assignDeliveryDriverWithAddress({ driverId: driver.id, assignments });
        
        console.log('✅ [FRONTEND] Backend response:', response);

        // Immediately remove assigned orders from pending list
        setDeliveries((prev) => {
          const updated = prev.filter((d) => !orderIds.includes(d.orderId));
          console.log('🗑️ [FRONTEND] Removed assigned orders from pending list:', updated);
          return updated;
        });

        // Refresh the delivery data to get updated counts
        setTimeout(async () => {
          try {
            console.log('🔄 [FRONTEND] Refreshing delivery data...');
            const [pendingRows, assignedRows] = await Promise.all([
              fetchPendingDeliveries(),
              fetchAssignedDeliveries(),
            ]);
            
            setDeliveries((prev) => {
              const otherDeliveries = prev.filter(d => d.status !== 'Pending' && d.status !== 'Assigned');
              const refreshed = [...otherDeliveries, ...pendingRows, ...assignedRows];
              console.log('✅ [FRONTEND] Data refreshed:', { 
                pendingCount: pendingRows.length, 
                assignedCount: assignedRows.length,
                totalDeliveries: refreshed.length
              });
              return refreshed;
            });
            
            setAssignedCount(assignedRows.length);
          } catch (e) {
            console.error('❌ [FRONTEND] Failed to refresh data:', e);
          }
        }, 500);

        // Increment assigned card count locally
        setAssignedCount((prev) => {
          const newCount = prev + orderIds.length;
          console.log('📊 [FRONTEND] Assigned count updated:', {
            before: prev,
            added: orderIds.length,
            after: newCount
          });
          return newCount;
        });
      }

      setIsDeliveryModalOpen(false);

      if (list.length > 1) {
        const count = list.length;
        setConfirmationMessage(
          `${driver.name} has been assigned to ${count} deliver${count === 1 ? "y" : "ies"}`
        );
      } else if (list.length === 1) {
        setConfirmationMessage(
          `${driver.name} has been assigned to Delivery for ${list[0].orderId}`
        );
      } else {
        setConfirmationMessage(`${driver.name} has been assigned`);
      }

      setIsConfirmationModalOpen(true);
      setSelectedIds(new Set());
    } catch (e) {
      // Basic error feedback; could be enhanced with a toast
      setConfirmationMessage("Failed to assign driver. Please try again.");
      setIsConfirmationModalOpen(true);
    }
  };

  // Helpers for date parsing and normalization (prefer yyyy/MM/dd)
  const parseDateStr = (s) => {
    if (!s || typeof s !== 'string') return null;
    const norm = s.trim().replace(/[.\-\s]+/g, '/');
    // Try yyyy/MM/dd first
    let m = /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/.exec(norm);
    if (m) {
      const yyyy = Number(m[1]);
      const mm = Number(m[2]);
      const dd = Number(m[3]);
      const d = new Date(yyyy, mm - 1, dd);
      return isNaN(d.getTime()) ? null : d;
    }
    // Fallback dd/MM/yyyy
    m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(norm);
    if (m) {
      const dd = Number(m[1]);
      const mm = Number(m[2]);
      const yyyy = Number(m[3]);
      const d = new Date(yyyy, mm - 1, dd);
      return isNaN(d.getTime()) ? null : d;
    }
    return null;
  };

  const normalize = (d) => (d ? new Date(d.getFullYear(), d.getMonth(), d.getDate()) : null);

  // Derive counts and filtered lists from deliveries
  // For the Completed tab, we intentionally leave End Date blank in the UI
  const normalizedDeliveries = deliveries.map((d) =>
    d.status === "Completed"
      ? { ...d, endDate: "" }
      : d
  );

  const statusFiltered = normalizedDeliveries.filter((d) => d.status === activeFilter);

  const shouldApplyDateFilter =
    (activeFilter === "Pending" && pendingDate) || (activeFilter === "Completed" && (fromDate || toDate));

  const dateFiltered = shouldApplyDateFilter
    ? statusFiltered.filter((row) => {
        const from = normalize(fromDate);
        const to = normalize(toDate);
        const p = normalize(pendingDate);
        const sd = normalize(parseDateStr(row.startDate));
        const ed = normalize(parseDateStr(row.endDate));

        const inRange = (date) => {
          if (!date) return false;
          if (from && date < from) return false;
          if (to && date > to) return false;
          return true;
        };

        if (activeFilter === "Pending") {
          if (!p || !sd) return false;
          return sd.getTime() === p.getTime();
        }

        return inRange(sd) || inRange(ed);
      })
    : statusFiltered;

  // Pagination based on filtered list
  const totalPages = Math.ceil(dateFiltered.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = dateFiltered.slice(startIndex, startIndex + itemsPerPage);

  // Stats for header cards
  const scheduledCount = deliveries.length;
  const pendingCount = deliveries.filter((d) => d.status === "Pending").length;
  const inTransitCount = deliveries.filter((d) => d.status === "In Transit").length;

  // Columns per filter
  const columnsPending = [
    { header: "Patient", accessor: "patient", width: "1.2fr" },
    { header: "Address", accessor: "address", width: "2fr" },
    { header: "Items", accessor: "items", width: "1.2fr" },
    { header: "Scheduled Time", accessor: "scheduledTime", width: "1fr" },
    {
      header: "Priority",
      accessor: "priority",
      render: (value) => (value ? <PriorityPill level={value} /> : null),
      width: "1fr",
    },
    {
      header: "",
      accessor: "select",
      width: "56px",
      headerRender: () => (
        <input
          type="checkbox"
          aria-label="Select all"
          checked={areAllCurrentSelected(paginatedData) && paginatedData.length > 0}
          onChange={() => toggleAllCurrent(paginatedData)}
          onClick={(e) => e.stopPropagation()}
        />
      ),
      render: (value, row) => (
        <input
          type="checkbox"
          checked={isSelected(row.orderId)}
          onChange={() => toggleOne(row.orderId)}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select ${row.orderId}`}
        />
      ),
    },
  ];

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className={styles.pageWrapper}>
      {/* Navbar positioned at top */}
      <Navbar />
      
      {/* Main content area that respects the sidebar flex layout */}
      <div className={styles.mainContent}>

        {/* Stats Cards */}
        <DeliveryStatsCardGrid
          scheduled={scheduledCount}
          pending={pendingCount}
          assigned={assignedCount}
          inTransit={inTransitCount}
        />
        
        {/* Filter Section (tabs only) */}
        <div className={styles.filterSection}>
          <FilterButtons
            buttons={filterButtons}
            activeButton={activeFilter}
            onButtonClick={(val) => {
              setActiveFilter(val);
              if (val === "Pending") {
                setFromDate(null);
                setToDate(null);
              } else if (val === "Completed") {
                setPendingDate(null);
              } else {
                setPendingDate(null);
                setFromDate(null);
                setToDate(null);
              }
              setCurrentPage(1); // reset pagination on filter change
            }}
          />
        </div>

        {/* Table Section */}
        <div className={styles.tableSection}>
          {activeFilter === "Pending" && (
            <div className={styles.dateAndActionRow}>
              <div className={styles.dateFiltersCard}>
                <div className={styles.dateFiltersRow}>
                  <div className={styles.inlineFilter}>
                    <span>Date</span>
                    <FormInput
                      label=""
                      type="date"
                      name="pendingDate"
                      value={pendingDate ? pendingDate.toISOString().slice(0, 10) : ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        setPendingDate(v ? new Date(v) : null);
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className={styles.inlineActions}>
                <button
                  className={styles.viewButton}
                  onClick={handleBatchAssignOpen}
                  disabled={selectedIds.size === 0}
                >
                  Assign Driver
                </button>
              </div>
            </div>
          )}

          {activeFilter === "Completed" && (
            <div className={styles.dateAndActionRow}>
              <div className={styles.dateFiltersCard}>
                <div className={styles.dateFiltersRow}>
                  <div className={styles.inlineFilter}>
                    <span>From</span>
                    <FormInput
                      label=""
                      type="date"
                      name="fromDate"
                      value={fromDate ? fromDate.toISOString().slice(0, 10) : ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        setFromDate(v ? new Date(v) : null);
                      }}
                    />
                  </div>
                  <div className={styles.inlineFilter}>
                    <span>To</span>
                    <FormInput
                      label=""
                      type="date"
                      name="toDate"
                      value={toDate ? toDate.toISOString().slice(0, 10) : ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        setToDate(v ? new Date(v) : null);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {loading && <div className={styles.loading}>Loading deliveries...</div>}
          {error && !loading && <div className={styles.error}>{error}</div>}
          <DeliveryTable
            variant={
              activeFilter === "In Transit"
                ? "inTransit"
                : activeFilter === "Completed"
                ? "completed"
                : "pending"
            }
            columns={
              activeFilter === "In Transit"
                ? columnsInTransit
                : activeFilter === "Completed"
                ? columnsCompleted
                : columnsPending
            }
            data={paginatedData}
            /* Row click disabled; actions via buttons only */
          />
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>

        {/* Modals */}
        <DeliveryModal
          isOpen={isDeliveryModalOpen}
          onClose={() => setIsDeliveryModalOpen(false)}
          deliveryData={selectedDelivery}
          selectedDeliveries={Array.from(selectedIds).map((id) =>
            dateFiltered.find((d) => d.orderId === id)
          ).filter(Boolean)}
          onAssign={handleDriverAssign}
          onSelectAddress={handleSelectAddress}
        />

        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          onClose={() => setIsConfirmationModalOpen(false)}
          message={confirmationMessage}
        />
      </div>
      
      {/* Footer positioned at bottom */}
      <Footer />
    </div>
  );
}

export default DeliveryDetails;